import { Component, inject, OnDestroy, Type } from '@angular/core';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { FeatureResolver } from '@luggagehero/shared/feature-resolution';
import { PRICING_SERVICE, PricingService, Review, SecuritySeal } from '@luggagehero/shared/interfaces';
import { SharedMomentService } from '@luggagehero/shared/services/moment';
import { SharedReviewsService } from '@luggagehero/shared/services/reviews';
import { afterPaymentInstructionKey } from '@luggagehero/shared/util';
import { filter, Subscription } from 'rxjs';

import { ModalService } from '../../../../../core/services/index';
import { ReviewBaseComponent } from '../../review.base-component';
import { BookingActionBaseComponent } from './booking-action.base-component';
import { BookingActionInfo } from './booking-action-info';
import { LeaveFeedbackActionBaseComponent } from './leave-feedback-action.base-component';
import { PickUpActionBaseComponent } from './pick-up-action.base-component';
import { ShowReceiptActionBaseComponent } from './show-receipt-action.base-component';

type UiStyle = 'primary' | 'success' | 'warning';

const PICKED_UP_STATUSES = ['CHECKED_OUT', 'PAID'];
const TICKER_MESSAGES = ['DO_NOT_TAKE_SCREENSHOT', 'LIVE_SCREEN_MUST_BE_PRESENTED_TO_STAFF'];

// AM-4364 Variant B — brief, deliberate beat after a star tap so the filled star registers
// on the green surface before the review modal morphs in (replaces the default 600ms HACK
// delay, which only matters for action transitions that close the modal). The green→white
// continuity animation lives in `leave-feedback-action.component.scss`.
const POST_VALIDATION_REVIEW_TRANSITION_DELAY_MS = 150;

@Component({
  template: '',
  standalone: false,
})
export abstract class ValidateBookingActionBaseComponent extends BookingActionBaseComponent implements OnDestroy {
  protected modalService = inject(ModalService);
  private momentService = inject(SharedMomentService);
  private pricingService = inject<PricingService>(PRICING_SERVICE);
  private reviewsService = inject(SharedReviewsService);
  private featureResolver = inject(FeatureResolver);

  private subscriptions: Subscription[];
  private _isCheckInSession = false;
  private _tickerMessages: string[];
  private _postValidationRating: number | null = null;

  /**
   * AM-4364 Variant B — currently-selected star rating in the bottom strip of the validate-booking
   * surface. Saved to the backend immediately on tap via `onPostValidationRatingChange`, then the
   * leave-feedback modal is mounted with the rating pre-filled.
   */
  public get postValidationRating(): number | null {
    return this._postValidationRating;
  }
  public set postValidationRating(value: number | null) {
    this._postValidationRating = value;
    this.cd.markForCheck();
  }

  public get isCheckInSession(): boolean {
    return this._isCheckInSession;
  }
  public set isCheckInSession(value: boolean) {
    this._isCheckInSession = value;
    this.cd.markForCheck();
  }

  public get tickerMessages(): string[] {
    if (AppConfig.IS_NO_SCREENSHOT_TICKER_ENABLED && this.shop && !this.shop.isSelfService) {
      return this._tickerMessages;
    }
    return null;
  }

  public get action() {
    switch (this.booking?.status) {
      case 'CHECKED_IN':
        return this.pickUpActionInfo;

      case 'CHECKED_OUT':
      case 'PAID':
        if (this.isPostValidationRatingActive) {
          return this.pickupRatingActionInfo;
        }
        return this.showReceiptActionInfo;

      default:
        return null;
    }
  }

  /**
   * AM-4364 — true when the post-validation rating strip should replace the
   * `VIEW_RECEIPT` CTA. All three gates must hold:
   *   - booking is in a picked-up status (PAID or CHECKED_OUT) — on CHECKED_IN (dropoff)
   *     the rating strip never renders, so this getter must be false to keep the
   *     `validate-booking-container--rating-active` host class off and avoid triggering
   *     pickup-only responsive hide rules (PIN label, access-text, dividers) on dropoff.
   *   - the effective pickup-review flow is `post-validation` — resolved by `FeatureResolver`,
   *     which prefers the `pickupReviewFlow` feature flag (a concrete value there is "the new
   *     normal" and wins for everyone) and otherwise reads the temporary per-user experiment
   *     variant. Variant A (`in-pickup-modal`) and the legacy flow both skip this branch.
   *   - the user hasn't already published a review for this booking — once published,
   *     the action getter falls back to `showReceiptActionInfo` so the receipt is
   *     accessible from this surface too (defensive; the primary access path is the
   *     order-summary card's `RECEIPT` CTA).
   */
  public get isPostValidationRatingActive(): boolean {
    // AM-4364 — never show the rating strip for no-show bookings (traveler didn't use the
    // service, often charged without showing up); the surface falls back to the receipt CTA.
    if (this.booking?.chargedNoShow) {
      return false;
    }
    const isPickedUp = PICKED_UP_STATUSES.includes(this.booking?.status);
    const isPostValidationFlow = this.featureResolver.pickupReviewFlow === 'post-validation';
    return isPickedUp && isPostValidationFlow && !this.review?.published;
  }

  public get pickUpActionInfo(): BookingActionInfo {
    return new BookingActionInfo('PickUpFromDropOffConfirmation')
      .withAction({ componentToShow: this.pickUpActionComponentType })
      .withCallToAction(this._translate('PROCEED_TO_PICKUP'));
  }

  /**
   * AM-4364 Variant B — `BookingActionInfo` rendered in the PAID bottom strip when the
   * post-validation rating flow is active. Intentionally does NOT use `withCallToAction`:
   * the template detects this action by `name` and renders the star-rating-input + Done
   * link directly. The `componentToShow` is the leave-feedback modal that mounts after a
   * star is tapped.
   */
  public get pickupRatingActionInfo(): BookingActionInfo {
    // No title/subtitle/state on purpose: the review view owns its full UI (AM-4364). Setting
    // them would render the shared modal chrome (success green line + "Payment succeeded" /
    // "Rate your experience" header + close X) around `lh-review`. Leaving them off hides that
    // header (`@if (modalTitle)`) and the green line (`modalSuccess`), so the green→white
    // wash animation resolves into a clean, chrome-less review surface.
    return new BookingActionInfo('PickupRatingFromValidateBooking')
      .withAction({ componentToShow: this.leaveFeedbackActionComponentType })
      .withTransitionDelay(POST_VALIDATION_REVIEW_TRANSITION_DELAY_MS)
      .withEnterFromSuccess(true)
      .withFullscreenReview(true);
  }

  public get showReceiptActionInfo(): BookingActionInfo {
    let callToAction: string, title: string, subtitle: string;
    // AM-4825 — the same instruction as every other receipt action: staff at a staffed shop,
    // the storage room at a self-service one.
    const instructionKey = afterPaymentInstructionKey({
      isSelfService: this.shop?.isSelfService,
      paidDirectly: this.booking.paidDirectly,
    });

    if (this.booking.paidDirectly) {
      const { total, currency } = this.latestOrder || this.booking.price.final;
      callToAction = this.pricingService.format(total, currency);
      title = 'PAY_IN_SHOP';
      subtitle = instructionKey;
    } else if (this.booking.paymentPostponedAt) {
      callToAction = 'GO_TO_PAYMENT';
      title = 'PAYMENT_FAILED';
      subtitle = null;
    } else {
      callToAction = 'VIEW_RECEIPT';
      title = 'PAYMENT_SUCCEEDED';
      subtitle = instructionKey;
    }

    return new BookingActionInfo('ShowReceiptFromPickupConfirmation')
      .withCallToAction(this._translate(callToAction))
      .withAction({ componentToShow: this.showReceiptActionComponentType })
      .withTitle(this._translate(title))
      .withSubtitle(subtitle && this._translate(subtitle))
      .withState(this.booking.paidDirectly || this.booking.paymentPostponedAt ? 'warning' : 'success');
  }

  /**
   * AM-4601 — the guest answered "Yes" to "Are you collecting your bags?": open the same pick-up modal
   * as the "Proceed to pickup" link, where the guest slides to check out.
   */
  public onCollectingBagsYes(): void {
    this.requestAction(this.pickUpActionInfo);
  }

  public get hasLongStorageRoomAccessText(): boolean {
    return this.shop?.storageRoomAccessText?.length > 60 ? true : false;
  }

  public get pickupTime(): string {
    if (!PICKED_UP_STATUSES.includes(this.booking?.status)) {
      return null;
    }
    return this.momentService.formatDate(this.booking.period.checkOut, 'calendarTime', this.booking.timezone);
  }

  public get luggageImage(): string {
    if (!this.booking || !this.booking.luggage.smartImages || this.booking.luggage.smartImages.length === 0) {
      return null;
    }
    return this.booking.luggage.smartImages[0].name;
  }

  public get securitySeals(): SecuritySeal[] {
    if (!this.booking?.luggage?.smartImages || this.booking.luggage.smartImages.length === 0) {
      return null;
    }
    return this.booking.luggage.smartImages[0].securitySeals;
  }

  public get title(): string {
    let value: string;

    switch (this.booking?.status) {
      case 'CHECKED_IN':
        value = 'DROPOFF_CONFIRMED';
        break;

      case 'CHECKED_OUT':
      case 'PAID':
        value = 'PICKUP_CONFIRMED';
        break;

      case 'CANCELLED':
        value = 'BOOKING_CANCELLED';
        break;

      default:
        value = 'BOOKING_CONFIRMED';
        break;
    }

    return this._translate(value);
  }

  public get instructions(): string {
    let value: string;

    switch (this.booking?.status) {
      case 'CHECKED_IN': {
        value = this.shop?.isSelfService ? 'DEPOSIT_BAGS_AT' : 'SHOW_THIS_AND_HAND_OVER_BAGS_AT';
        break;
      }
      case 'CHECKED_OUT':
      case 'PAID': {
        // TODO: How to handle failed payments at self-service locations (potentially no staff to take direct payment)?
        value = this.booking.paidDirectly
          ? 'SHOW_THIS_AND_PAY_DIRECTLY_AT'
          : this.shop?.isSelfService
            ? 'COLLECT_BAGS_AT'
            : 'SHOW_THIS_TO_COLLECT_BAGS_AT';
        break;
      }
    }

    return value && this._translate(value);
  }

  public get statusIcon(): string {
    return this.style === 'warning' ? 'exclamation' : 'check';
  }

  public get style(): UiStyle {
    if (['CHECKED_OUT', 'PAID'].includes(this.booking?.status)) {
      // Only `paidDirectly` flips the surface to orange (the partner is being asked to
      // collect cash on-site and needs the visual cue). `paymentPostponedAt` is
      // intentionally kept on the green/success surface — that's a "we'll chase the
      // customer for payment ourselves via email/SMS later" path, so we don't want to
      // create payment-issue friction at the partner counter. The traveler sees the
      // green confirmation + a discreet "Go to payment" link, can take their bags, and
      // resolves the payment outside the partner's awareness. Do not unify the two
      // branches without product input.
      return this.booking.paidDirectly ? 'warning' : 'success';
    }
    return 'primary';
  }

  public get maxTagsCollapsed(): number {
    return AppConfig.MAX_ITEMS_IN_COLLAPSED_TAG_LIST;
  }

  protected abstract get pickUpActionComponentType(): Type<PickUpActionBaseComponent>;
  protected abstract get showReceiptActionComponentType(): Type<ShowReceiptActionBaseComponent>;
  protected abstract get leaveFeedbackActionComponentType(): Type<LeaveFeedbackActionBaseComponent>;

  /**
   * AM-4364 Variant B — user tapped a star in the bottom strip. Persist the rating
   * immediately (so we capture it even if the user dismisses the leave-feedback modal
   * without submitting), then dispatch the leave-feedback action so its modal mounts
   * on top with the rating pre-filled.
   *
   * Mirrors `PickUpActionBaseComponent.onRatingChange` (Variant A) so the two surfaces
   * share the same save semantics and Customer.io events.
   */
  public async onPostValidationRatingChange(value: number): Promise<void> {
    this._postValidationRating = value;

    try {
      let review: Review = this.review;
      if (!review) {
        review = this.buildReviewStub();
      }
      review.rating = value;
      review.satisfaction = ReviewBaseComponent.starsToSatisfaction(value);

      const saved = review._id
        ? await this.reviewsService.updateReview(review)
        : await this.reviewsService.addReview(review);

      this.review = saved;
      this.cd.markForCheck();

      // Hand off to the leave-feedback modal so the user can optionally add a comment.
      // The action's `componentToShow` triggers `manage-booking-timeline` to close this
      // modal and open the leave-feedback one; the rating arrives there via the `review`
      // @Input (already populated above).
      this.requestAction(this.pickupRatingActionInfo);
    } catch (_err) {
      // Soft-fail: surface to user via review-modal flow on the next tap.
      // Errors during the immediate save are non-blocking — the user can retry by re-tapping.
      this._postValidationRating = null;
      this.cd.markForCheck();
    }
  }

  private buildReviewStub(): Review {
    return {
      bookingId: this.booking._id,
      shopId: this.booking.shopId,
      userName: this.booking.userName,
      userPicture: this.booking.userPicture,
      shopName: this.booking.shopName,
      published: false,
    } as Review;
  }

  public async onBaseInit(): Promise<void> {
    await super.onBaseInit();

    this._tickerMessages = TICKER_MESSAGES.map((key) => this._translate(key));

    // AM-4364 Variant B — load any existing review so `isPostValidationRatingActive` can
    // suppress the rating strip once the user has already published a review (e.g. after
    // returning to the surface from another part of the app).
    if (this.isPostValidationRatingActive && this.booking?._id && !this.review) {
      try {
        const existing = await this.reviewsService.getReview(this.booking._id);
        if (existing) {
          this.review = existing;
        }
      } catch (_err) {
        // Soft-fail: if review fetch errors we just default to showing the rating strip.
      }
    }

    // AM-4364 Variant B — seed the strip's star input from any existing rating so returning to
    // this surface (or a page refresh) shows the rating the user already gave, instead of empty
    // stars animating in from scratch. The rating persists fine (the feedback view reads it); it
    // was only the strip that never initialised from it. `review` was either loaded just above
    // or supplied via the `review` @Input.
    if (this.review?.rating && !this.postValidationRating) {
      this.postValidationRating = this.review.rating;
    }

    this.subscriptions = [];
    this.subscriptions.push(
      this.bookingService.bookingEvent$
        .pipe(filter((e) => e?.eventType === 'bagsDroppedOff'))
        .subscribe((_e) => (this.isCheckInSession = true)),
    );
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();

    this.subscriptions?.forEach((s) => s.unsubscribe());
  }
}
